import { Chip } from "@mui/material";
import {
  TicketPriority,
  TicketStatus,
  TicketSummary,
} from "../../../utils/icTicket";

export const PRIMARY = "#9E1F63FF";
export const CARD_BORDER = "1px solid #ececf0";

type ChipTone = { label: string; bg: string; fg: string };

// Labels from IC_Ticket's integration guide. WAITING depends on the reason:
// CLARIFICATION means the team is waiting on the requester.
export const statusTone = (
  ticket: Pick<TicketSummary, "status" | "needsReply">,
): ChipTone => {
  switch (ticket.status) {
    case "NEW":
    case "TRIAGED":
      return { label: "Open", bg: "#e8f0fe", fg: "#1a56c4" };
    case "IN_PROGRESS":
      return { label: "In progress", bg: "#fbf5e6", fg: "#8a6d00" };
    case "WAITING":
      return ticket.needsReply
        ? { label: "Needs your reply", bg: "#fdecea", fg: "#c62828" }
        : { label: "Waiting", bg: "#fbf5e6", fg: "#8a6d00" };
    case "FIXED":
      return { label: "Resolved", bg: "#e7f5ec", fg: "#1e7b3c" };
    case "RETEST":
      return { label: "Resolved – please verify", bg: "#e7f5ec", fg: "#1e7b3c" };
    case "CLOSED":
    case "REJECTED":
      return { label: "Closed", bg: "#f0f0f3", fg: "#5a5a66" };
    default:
      return { label: String(ticket.status || "Unknown"), bg: "#f0f0f3", fg: "#5a5a66" };
  }
};

export const StatusChip = ({
  ticket,
}: {
  ticket: Pick<TicketSummary, "status" | "needsReply">;
}) => {
  const tone = statusTone(ticket);
  return (
    <Chip
      size="small"
      label={tone.label}
      sx={{ fontWeight: 700, backgroundColor: tone.bg, color: tone.fg }}
    />
  );
};

const PRIORITY_TONE: Record<TicketPriority, { bg: string; fg: string }> = {
  LOW: { bg: "#f0f0f3", fg: "#5a5a66" },
  MEDIUM: { bg: "#e8f0fe", fg: "#1a56c4" },
  HIGH: { bg: "#fff1e0", fg: "#b45309" },
  CRITICAL: { bg: "#fdecea", fg: "#c62828" },
};

export const priorityLabel = (priority: string) =>
  priority ? priority.charAt(0) + priority.slice(1).toLowerCase() : "—";

export const PriorityChip = ({ priority }: { priority: TicketPriority }) => {
  const tone = PRIORITY_TONE[priority] ?? PRIORITY_TONE.LOW;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={priorityLabel(priority)}
      sx={{ fontWeight: 600, color: tone.fg, borderColor: tone.fg, backgroundColor: tone.bg }}
    />
  );
};

export const NeedsReplyBadge = () => (
  <Chip
    size="small"
    label="Needs your reply"
    sx={{ fontWeight: 700, backgroundColor: "#c62828", color: "white", height: 22 }}
  />
);

// List filters → IC_Ticket's stored statuses. "Needs your reply" asks the
// server for WAITING and keeps only needsReply rows (the server cannot filter
// on waitingReason).
export type TicketFilterKey = "all" | "open" | "reply" | "resolved" | "closed";
export const TICKET_FILTERS: {
  key: TicketFilterKey;
  label: string;
  statuses: TicketStatus[];
  needsReplyOnly?: boolean;
}[] = [
  { key: "all", label: "All", statuses: [] },
  { key: "open", label: "Open", statuses: ["NEW", "TRIAGED", "IN_PROGRESS", "WAITING"] },
  { key: "reply", label: "Needs your reply", statuses: ["WAITING"], needsReplyOnly: true },
  { key: "resolved", label: "Resolved", statuses: ["FIXED", "RETEST"] },
  { key: "closed", label: "Closed", statuses: ["CLOSED", "REJECTED"] },
];
